import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ConfirmDelete } from "@/components/common/ConfirmDelete";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";

const NUM = [["monthly", "Loyer mensuel"], ["first_rent", "1er loyer (avant prime)"], ["buyout", "Option d'achat"], ["months", "Durée (mois)"], ["km_year", "km / an"]];

const TeslaForm = () => {
  const { data } = useQuery({ queryKey: ["admin-tesla"], queryFn: () => api.get("/admin/offers/tesla").then((r) => r.data) });
  const [v, setV] = useState(null);
  useEffect(() => { if (data) { setV(data); } }, [data]);
  if (!v) return null;
  const setM = (i, k, val) => setV({ ...v, models: v.models.map((m, j) => (j === i ? { ...m, [k]: k === "name" ? val : Number(val) } : m)) });
  const save = async () => { try { await api.put("/admin/offers/tesla", v); toast.success("Offres Tesla enregistrées"); } catch (e) { toast.error(formatApiError(e)); } };
  return (
    <section className="card-surface space-y-3 p-5" data-testid="admin-tesla">
      <h2 className="font-extrabold">Offres Tesla (LOA)</h2>
      <div className="grid gap-2 sm:grid-cols-3"><Input type="date" value={v.as_of} onChange={(e) => setV({ ...v, as_of: e.target.value })} data-testid="tesla-as-of" /><Input value={v.source} onChange={(e) => setV({ ...v, source: e.target.value })} placeholder="Source" /><label className="text-[11px] font-semibold">Entretien Tesla €/mois<Input type="number" min="0" value={v.maintenance_month ?? 10} onChange={(e) => setV({ ...v, maintenance_month: Number(e.target.value) })} data-testid="tesla-maintenance" /></label></div>
      {v.models.map((m, i) => (
        <div key={m.key} className="grid gap-2 rounded-xl bg-muted/50 p-3 sm:grid-cols-6" data-testid={`tesla-model-${m.key}`}>
          <Input value={m.name} onChange={(e) => setM(i, "name", e.target.value)} />
          {NUM.map(([k, l]) => <label key={k} className="text-[11px] font-semibold">{l}<Input type="number" value={m[k]} onChange={(e) => setM(i, k, e.target.value)} /></label>)}
          <label className="text-[11px] font-semibold sm:col-span-6">Photo (URL ou /cars/…)<Input value={m.image || ""} onChange={(e) => setV({ ...v, models: v.models.map((x, j) => (j === i ? { ...x, image: e.target.value || null } : x)) })} data-testid={`tesla-image-${m.key}`} /></label>
        </div>))}
      <Button className="rounded-full" onClick={save} data-testid="tesla-save">Enregistrer</Button>
    </section>
  );
};

const ProjectForm = ({ onDone }) => {
  const [v, setV] = useState({ title: "", category: "", description: "", goal: "" });
  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/impact-projects", { ...v, goal: v.goal ? Number(v.goal) : null }); setV({ title: "", category: "", description: "", goal: "" }); onDone(); toast.success("Projet ajouté"); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <form onSubmit={add} className="grid gap-2 sm:grid-cols-3" data-testid="project-form">
      <Input value={v.title} onChange={(e) => setV({ ...v, title: e.target.value })} placeholder="Titre" required minLength={3} data-testid="project-title" />
      <Input value={v.category} onChange={(e) => setV({ ...v, category: e.target.value })} placeholder="Catégorie (ex. Pompiers)" required minLength={2} data-testid="project-category" />
      <Input type="number" min="1" value={v.goal} onChange={(e) => setV({ ...v, goal: e.target.value })} placeholder="Objectif € (facultatif)" data-testid="project-goal" />
      <Textarea value={v.description} onChange={(e) => setV({ ...v, description: e.target.value })} placeholder="Description" required minLength={10} className="sm:col-span-3" data-testid="project-description" />
      <Button type="submit" className="rounded-full sm:col-span-3" data-testid="project-add">Ajouter le projet</Button>
    </form>
  );
};

export const OffersAdmin = () => {
  const qc = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ["impact-projects"], queryFn: () => api.get("/impact-projects").then((r) => r.data) });
  const refresh = () => qc.invalidateQueries({ queryKey: ["impact-projects"] });
  return (
    <div className="space-y-6" data-testid="admin-offers">
      <TeslaForm />
      <section className="card-surface space-y-3 p-5">
        <h2 className="font-extrabold">Projets à soutenir</h2>
        <ProjectForm onDone={refresh} />
        <ul className="divide-y divide-border text-sm">{data.map((p) => (
          <li key={p.id} className="flex items-center gap-2 py-2" data-testid={`admin-project-${p.id}`}><span className="min-w-0 flex-1"><b>{p.title}</b> <span className="text-xs text-muted-foreground">· {p.category}{p.example ? " · Exemple" : ""}</span></span>
            <ConfirmDelete title="Supprimer ce projet ?" text={p.title} testid={`admin-project-delete-${p.id}`} onConfirm={async () => { await api.delete(`/admin/impact-projects/${p.id}`); refresh(); }} /></li>))}</ul>
      </section>
    </div>
  );
};
